import { trimStr } from '../cfoFormat'
import type { CfoAnalysisAction, CfoAnalysisContext, CfoAnalysisVisualSpec } from './types'

// Deterministic drill-down chips. The agent's own `followups` always take
// precedence (see useCfoCopilot); these are the instant, always-present
// fallback so the modal never shows an answer with no next step — even before
// the AI turn lands. Keyed by metric/detector, with sensible generic defaults.

const ratioChips = (label: string, outOfRange: boolean): CfoAnalysisAction[] => {
  const subject = label || 'this ratio'
  const chips: CfoAnalysisAction[] = []
  if (outOfRange) {
    chips.push({ label: 'Why is it out of range?', prompt: `Why is ${subject} outside its healthy range, and what is driving it?` })
  } else {
    chips.push({ label: 'What is driving this?', prompt: `What is driving ${subject} right now, and is the trend a concern?` })
  }
  chips.push({ label: 'Which accounts move it?', prompt: `Which accounts or balances move ${subject} the most this period?` })
  chips.push({ label: 'Show the 12-month trend', prompt: `Walk me through the 12-month trend in ${subject} and what changed.` })
  chips.push({ label: 'How do peers compare?', prompt: `How does our ${subject} compare to the industry benchmark, and is the gap material?` })
  return chips
}

const METRIC_CHIPS: Record<string, CfoAnalysisAction[]> = {
  net_income: [
    { label: 'What drove the change?', prompt: 'What drove the change in net income this month? Show the profit bridge.' },
    { label: 'Break down by category', prompt: 'Break net income down by revenue, COGS, and operating expense category.' },
    { label: 'Show the GL transactions', prompt: 'Show the largest GL transactions behind the net income change.' },
  ],
  revenue: [
    { label: 'What changed in revenue?', prompt: 'What changed in revenue this month, and which lines drove it?' },
    { label: 'Break down by source', prompt: 'Break revenue down by source/category for this period.' },
    { label: 'Compare to prior periods', prompt: 'Compare revenue to the prior 3 months and the same month last year.' },
  ],
  gross_margin: [
    { label: 'What moved margin?', prompt: 'What moved gross margin this month — price, mix, or cost of goods?' },
    { label: 'Show COGS detail', prompt: 'Break down cost of goods sold and show what changed.' },
  ],
  operating_expenses: [
    { label: 'Which categories grew?', prompt: 'Which operating expense categories grew the most this month?' },
    { label: 'Show the GL transactions', prompt: 'Show the largest operating-expense GL transactions this period.' },
  ],
}

const GENERIC_CHIPS: CfoAnalysisAction[] = [
  { label: 'What changed and why?', prompt: 'What changed here this month, and why?' },
  { label: 'Show the supporting detail', prompt: 'Show the supporting detail behind this number.' },
  { label: 'What should I watch next?', prompt: 'What should I watch next on this, and what is the next step?' },
]

/**
 * Resolve fallback drill-down chips for an analysis session, used only when the
 * agent has not (yet) emitted `followups`.
 */
export function fallbackChipsForSession(args: {
  context: CfoAnalysisContext
  visual?: CfoAnalysisVisualSpec | null
}): CfoAnalysisAction[] {
  const { context, visual } = args
  const metricId = trimStr(context.metricId)
  const label = trimStr(context.metricLabel) || metricId.replace(/_/g, ' ')

  const isRatio =
    visual?.kind === 'ratio_health' ||
    context.grounding?.source === 'ratio_card' ||
    trimStr(context.sourceComponent).startsWith('ratio')
  if (isRatio) {
    const assessment = visual?.kind === 'ratio_health' ? visual.assessment : context.grounding?.assessment
    const outOfRange = assessment === 'concern' || assessment === 'watch'
    return ratioChips(label, outOfRange).slice(0, 4)
  }

  if (metricId && METRIC_CHIPS[metricId]) return METRIC_CHIPS[metricId]

  return GENERIC_CHIPS
}
